import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Check, Sparkles, Briefcase, User, Share2, ShoppingCart, BarChart3,
  Globe, Music, Mail, Link as LinkIcon, Loader2, X,
} from 'lucide-react';
import { BRAND_ICONS } from '../linkpages/brandIcons.js';
import { THEMES, DEFAULT_DESIGN, type LinkItem, type LinkPage } from '../linkpages/themes.js';
import { useLP } from '../linkpages/texts.js';
import { readError } from './LinkPages.js';

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;
const newId = () => Math.random().toString(36).slice(2, 12);

/** Wizard platforms; `icon` must be a value the API whitelist accepts (ICON_OPTIONS). */
const PLATFORMS = [
  { id: 'instagram', icon: 'instagram', label: 'Instagram', handle: true, prefix: 'https://instagram.com/' },
  { id: 'whatsapp', icon: 'whatsapp', label: 'WhatsApp', handle: false, prefix: 'https://wa.me/' },
  { id: 'tiktok', icon: 'tiktok', label: 'TikTok', handle: true, prefix: 'https://tiktok.com/' },
  { id: 'youtube', icon: 'youtube', label: 'YouTube', handle: false, prefix: '' },
  { id: 'website', icon: 'globe', label: 'Website', handle: false, prefix: '' },
  { id: 'spotify', icon: 'music', label: 'Spotify', handle: false, prefix: '' },
  { id: 'x', icon: 'twitter', label: 'X', handle: true, prefix: 'https://x.com/' },
  { id: 'facebook', icon: 'facebook', label: 'Facebook', handle: false, prefix: '' },
  { id: 'telegram', icon: 'telegram', label: 'Telegram', handle: true, prefix: 'https://t.me/' },
  { id: 'linkedin', icon: 'linkedin', label: 'LinkedIn', handle: false, prefix: '' },
  { id: 'github', icon: 'github', label: 'GitHub', handle: true, prefix: 'https://github.com/' },
  { id: 'mail', icon: 'mail', label: 'E-Mail', handle: false, prefix: 'mailto:' },
] as const;

type PlatformId = (typeof PLATFORMS)[number]['id'];

function PlatformGlyph({ icon, size = 22 }: { icon: string; size?: number }) {
  const brand = BRAND_ICONS[icon];
  if (brand) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d={brand} />
      </svg>
    );
  }
  const Generic = { globe: Globe, music: Music, mail: Mail }[icon] ?? LinkIcon;
  return <Generic size={size} aria-hidden="true" />;
}

interface WizardLink {
  platform: PlatformId | 'link'
  label: string
  input: string
}

const GOALS = [
  { id: 'creator', icon: Sparkles },
  { id: 'business', icon: Briefcase },
  { id: 'personal', icon: User },
] as const;

const INTENTIONS = [
  { id: 'share', icon: Share2 },
  { id: 'earn', icon: ShoppingCart },
  { id: 'grow', icon: BarChart3 },
  { id: 'else', icon: Sparkles },
] as const;

const STEPS = 5;

export default function LinkPageWizard() {
  const lp = useLP();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [goal, setGoal] = useState<string | null>(null);
  const [intentions, setIntentions] = useState<string[]>([]);
  const [platforms, setPlatforms] = useState<PlatformId[]>([]);
  const [links, setLinks] = useState<WizardLink[]>([]);
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [theme, setTheme] = useState('aurora');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  type SlugStatus = 'idle' | 'invalid' | 'checking' | 'available' | 'taken';
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
  const slugCheckSeq = useRef(0);

  // live slug availability check (debounced), so a taken address is flagged while typing
  useEffect(() => {
    const clean = slug.trim().toLowerCase();
    if (!clean) { setSlugStatus('idle'); return undefined; }
    if (!SLUG_RE.test(clean)) { setSlugStatus('invalid'); return undefined; }
    setSlugStatus('checking');
    const seq = ++slugCheckSeq.current;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const res = await fetch(`/api/link-pages/slug-available/${encodeURIComponent(clean)}`, { credentials: 'include' });
          if (!res.ok) { if (seq === slugCheckSeq.current) setSlugStatus('idle'); return; }
          const data = (await res.json()) as { available?: boolean };
          if (seq === slugCheckSeq.current) setSlugStatus(data.available ? 'available' : 'taken');
        } catch {
          if (seq === slugCheckSeq.current) setSlugStatus('idle');
        }
      })();
    }, 400);
    return () => clearTimeout(timer);
  }, [slug]);

  const next = () => setStep((s) => Math.min(STEPS, s + 1));
  const back = () => (step === 1 ? navigate('/links') : setStep((s) => s - 1));

  const enterLinksStep = () => {
    // pre-fill rows from the chosen platforms the first time we reach step 4
    if (links.length === 0) {
      setLinks([
        ...platforms.map((p) => ({ platform: p, label: PLATFORMS.find((x) => x.id === p)?.label ?? p, input: '' })),
        ...[0, 1, 2].map(() => ({ platform: 'link' as const, label: '', input: '' })),
      ]);
    }
    next();
  };

  const canContinue = useMemo(() => {
    if (step === 1) return !!goal;
    if (step === 2) return intentions.length > 0;
    if (step === 3) return platforms.length > 0;
    if (step === 4) return slugStatus === 'available' && links.some((l) => l.input.trim());
    return !!theme;
  }, [step, goal, intentions, platforms, slugStatus, links, theme]);

  async function finish() {
    setError('');
    const clean = slug.trim().toLowerCase();
    if (!SLUG_RE.test(clean)) { setError(lp('slugInvalid')); return; }
    setBusy(true);
    try {
      const pageTitle = title.trim() || clean;
      const res = await fetch('/api/link-pages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ slug: clean, title: pageTitle }),
      });
      if (res.status === 402) { setError(lp('wizLimit')); return; }
      if (res.status === 409) {
        // lost the race: someone took the slug after the live check — back to the address field
        setSlugStatus('taken');
        setStep(4);
        return;
      }
      if (!res.ok) { setError(await readError(res)); return; }
      const page = (await res.json()) as LinkPage;

      const items: LinkItem[] = links
        .filter((l) => l.input.trim())
        .map((l) => {
          const p = PLATFORMS.find((x) => x.id === l.platform);
          let url = l.input.trim();
          if (p?.handle && url.startsWith('@')) url = p.prefix + url.slice(1);
          else if (p?.prefix && !/^[a-z][a-z0-9+.-]*:/i.test(url) && !url.includes('.')) url = p.prefix + url.replace(/^@/, '');
          return {
            id: newId(), type: 'link', enabled: true,
            label: l.label.trim() || p?.label || url,
            url,
            icon: p?.icon ?? 'link',
          };
        });
      if (items.length > 0 || theme !== DEFAULT_DESIGN.theme) {
        const put = await fetch(`/api/link-pages/${page.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            ...page,
            title: pageTitle,
            design: { ...DEFAULT_DESIGN, ...page.design, theme },
            links: items,
          }),
        });
        if (!put.ok) { setError(await readError(put)); return; }
      }
      navigate(`/links/${page.id}`);
    } finally {
      setBusy(false);
    }
  }

  const onContinue = () => {
    if (step === 3) enterLinksStep();
    else if (step === STEPS) void finish();
    else next();
  };

  const inputCls = 'w-full rounded-xl bg-panel border border-line px-4 py-3 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30';
  const cardBtn = 'w-full flex items-center gap-4 rounded-2xl border-2 bg-panel px-5 py-4 text-left transition-colors';

  return (
    <div className="min-h-screen bg-canvas text-ink flex flex-col">
      <header className="relative flex items-center justify-between px-6 pt-5">
        <button type="button" onClick={back} className="flex w-16 items-center gap-1 text-sm font-semibold text-ink-muted hover:text-ink">
          <ArrowLeft className="h-4 w-4" /> {lp('wizBack')}
        </button>
        <div className="absolute left-1/2 h-1 w-24 -translate-x-1/2 overflow-hidden rounded-full bg-raised">
          <div className="h-full rounded-full bg-accent-500 transition-all duration-500" style={{ width: `${(step / STEPS) * 100}%` }} />
        </div>
        <span className="w-16 text-right text-xs text-ink-faint">{step}/{STEPS}</span>
      </header>

      <main className="flex flex-1 flex-col items-center px-6 pb-8 pt-8 md:pt-12">
        <h1 className="whitespace-pre-line text-center text-2xl font-extrabold tracking-tight md:text-4xl">{lp(`wizTitle${step}`)}</h1>
        <p className="mt-2 text-center text-sm text-ink-muted md:mt-3">{lp(`wizSub${step}`)}</p>

        <div className={`mt-6 w-full md:mt-10 ${step === 3 || step === 5 ? 'max-w-3xl' : 'max-w-xl'}`}>
          {step === 1 && (
            <div className="space-y-3">
              {GOALS.map((g) => (
                <button key={g.id} type="button" onClick={() => setGoal(g.id)}
                  className={`${cardBtn} ${goal === g.id ? 'border-accent-400' : 'border-line hover:border-line-strong'}`}
                  aria-pressed={goal === g.id}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-500/15 text-accent-500">
                    <g.icon className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-bold">{lp(`wizGoal_${g.id}`)}</span>
                    <span className="mt-0.5 block text-sm text-ink-muted">{lp(`wizGoal_${g.id}_desc`)}</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              {INTENTIONS.map((i) => {
                const active = intentions.includes(i.id);
                return (
                  <button key={i.id} type="button" aria-pressed={active}
                    onClick={() => setIntentions(active ? intentions.filter((x) => x !== i.id) : [...intentions, i.id])}
                    className={`${cardBtn} ${active ? 'border-accent-400' : 'border-line hover:border-line-strong'}`}>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-500/15 text-accent-500">
                      <i.icon className="h-5 w-5" />
                    </span>
                    <span className="font-semibold">{lp(`wizIntent_${i.id}`)}</span>
                    {active && <Check className="ml-auto h-4 w-4 text-accent-500" />}
                  </button>
                );
              })}
            </div>
          )}

          {step === 3 && (
            <div className="mx-auto grid max-w-lg grid-cols-3 gap-3">
              {PLATFORMS.map((p) => {
                const sel = platforms.includes(p.id);
                return (
                  <button key={p.id} type="button" aria-pressed={sel}
                    onClick={() => setPlatforms(sel ? platforms.filter((x) => x !== p.id) : platforms.length >= 5 ? platforms : [...platforms, p.id])}
                    className={`relative flex flex-col items-center gap-2.5 rounded-2xl border-2 bg-panel py-5 transition-colors ${sel ? 'border-accent-400' : 'border-line hover:border-line-strong'}`}>
                    {sel && (
                      <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-accent-600 text-white">
                        <Check className="h-3 w-3" />
                      </span>
                    )}
                    <PlatformGlyph icon={p.icon} size={26} />
                    <span className="text-xs font-semibold">{p.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <div className="rounded-2xl border border-line bg-panel p-4">
                <label htmlFor="wiz-slug" className="block text-xs font-semibold uppercase tracking-wide text-ink-muted">{lp('wizAddress')}</label>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="text-sm text-ink-muted">/l/</span>
                  <input id="wiz-slug" className={inputCls} value={slug} maxLength={40} autoComplete="off"
                    placeholder={lp('slugPlaceholder')}
                    onChange={(e) => setSlug(e.target.value.toLowerCase())} />
                </div>
                {slugStatus !== 'idle' && (
                  <p aria-live="polite" className={`mt-1.5 flex items-center gap-1.5 text-xs ${slugStatus === 'available' ? 'text-success-500' : slugStatus === 'checking' ? 'text-ink-faint' : 'text-danger-500'}`}>
                    {slugStatus === 'checking' && <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {lp('wizSlugChecking')}</>}
                    {slugStatus === 'available' && <><Check className="h-3.5 w-3.5" /> {lp('wizSlugAvailable')}</>}
                    {slugStatus === 'taken' && <><X className="h-3.5 w-3.5" /> {lp('wizSlugTaken')}</>}
                    {slugStatus === 'invalid' && <><X className="h-3.5 w-3.5" /> {lp('slugInvalid')}</>}
                  </p>
                )}
                <input aria-label={lp('titleField')} className={`${inputCls} mt-2`} value={title} maxLength={100}
                  placeholder={lp('wizTitlePlaceholder')}
                  onChange={(e) => setTitle(e.target.value)} />
              </div>

              {platforms.length > 0 && <h3 className="text-center text-sm font-bold">{lp('wizYourSelections')}</h3>}
              <div className="space-y-2.5">
                {links.filter((l) => l.platform !== 'link').map((l) => {
                  const idx = links.findIndex((x) => x === l);
                  const p = PLATFORMS.find((x) => x.id === l.platform);
                  return (
                    <div key={l.platform} className="flex items-center gap-3">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-line bg-panel text-ink">
                        <PlatformGlyph icon={p?.icon ?? 'link'} />
                      </span>
                      <input className={inputCls} value={l.input}
                        placeholder={p?.handle ? '@username' : 'https://…'}
                        onChange={(e) => setLinks(links.map((x, i) => (i === idx ? { ...x, input: e.target.value } : x)))} />
                    </div>
                  );
                })}
              </div>

              <h3 className="text-center text-sm font-bold">{lp('wizAdditionalLinks')}</h3>
              <div className="space-y-2.5">
                {links.filter((l) => l.platform === 'link').map((l) => {
                  const idx = links.findIndex((x) => x === l);
                  return (
                    <div key={idx} className="flex items-center gap-2.5">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-line bg-panel text-ink-muted">
                        <LinkIcon className="h-5 w-5" />
                      </span>
                      <input className={`${inputCls} w-40 flex-none`} value={l.label} maxLength={100}
                        placeholder={lp('label')}
                        onChange={(e) => setLinks(links.map((x, i) => (i === idx ? { ...x, label: e.target.value } : x)))} />
                      <input className={inputCls} value={l.input} placeholder="https://…"
                        onChange={(e) => setLinks(links.map((x, i) => (i === idx ? { ...x, input: e.target.value } : x)))} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="grid grid-cols-3 gap-2.5 md:gap-3">
              {Object.entries(THEMES).filter(([k]) => k !== 'custom').map(([key, t]) => {
                const r = t as { background: string; backgroundEnd: string; textColor: string; accent: string };
                const active = theme === key;
                return (
                  <button key={key} type="button" aria-pressed={active} onClick={() => setTheme(key)}
                    className={`rounded-xl p-1 transition-all ${active ? 'ring-2 ring-accent-400' : 'ring-1 ring-line hover:ring-line-strong'}`}>
                    <div className="flex h-28 flex-col items-center gap-1 overflow-hidden rounded-lg px-2 pt-3 md:h-32"
                      style={{ background: `linear-gradient(160deg, ${r.background}, ${r.backgroundEnd})`, color: r.textColor }}>
                      <div className="h-6 w-6 rounded-full" style={{ background: r.accent }} />
                      <div className="mt-0.5 truncate text-[10px] font-bold">{t.label}</div>
                      {[0, 1, 2].map((i) => (
                        <div key={i} className="h-2.5 w-full rounded" style={{ background: r.accent, opacity: 0.9 }} />
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {error && (
          <div role="alert" className="mt-6 w-full max-w-xl rounded-lg border border-danger-500/30 bg-danger-100 px-4 py-3 text-sm text-danger-600 dark:bg-danger-500/10 dark:text-danger-300">
            {error}
          </div>
        )}
      </main>

      <footer className="flex justify-center px-6 pb-10">
        <button type="button" disabled={!canContinue || busy} onClick={onContinue}
          className="flex w-full max-w-xl items-center justify-center gap-2 rounded-full bg-accent-600 py-3.5 text-base font-semibold text-white transition-opacity hover:bg-accent-500 disabled:cursor-not-allowed disabled:opacity-40">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {step === STEPS ? lp('wizFinish') : lp('wizContinue')}
        </button>
      </footer>
    </div>
  );
}
